import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { mapItemsApi, notificationsApi } from '../lib/api';
import EmergencyMap from '../components/map/EmergencyMap';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Radio,
  Bell,
  Map,
  AlertTriangle,
  Construction,
  PartyPopper,
  Ban,
  LogIn,
  RefreshCw,
  X,
  ArrowLeft,
  Lock,
  Printer,
} from 'lucide-react';

const PublicMapPage = () => {
  const [mapItems, setMapItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = async () => {
    try {
      const [itemsRes, notifsRes] = await Promise.all([
        mapItemsApi.getActive(),
        notificationsApi.getAll(),
      ]);
      setMapItems(itemsRes.data);
      setNotifications(notifsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'road_closure': return <Ban className="w-4 h-4" />;
      case 'detour': return <Construction className="w-4 h-4" />;
      case 'parade_route': return <PartyPopper className="w-4 h-4" />;
      case 'closed_area': return <AlertTriangle className="w-4 h-4" />;
      default: return <Map className="w-4 h-4" />;
    }
  };

  const getItemColor = (type) => {
    switch (type) {
      case 'road_closure': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'detour': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'parade_route': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'closed_area': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-blue-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-zinc-500 text-white';
    }
  };

  const itemTypeCounts = {
    road_closure: mapItems.filter(i => i.item_type === 'road_closure').length,
    detour: mapItems.filter(i => i.item_type === 'detour').length,
    parade_route: mapItems.filter(i => i.item_type === 'parade_route').length,
    closed_area: mapItems.filter(i => i.item_type === 'closed_area').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="public-map-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-900/95 backdrop-blur-xl border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 lg:px-6 py-3">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                <Radio className="w-5 h-5 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="font-headings font-bold text-sm uppercase tracking-wide">Anaconda-Deer Lodge</h1>
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Emergency Services</p>
              </div>
            </Link>
            <div className="h-6 w-px bg-zinc-700 hidden sm:block" />
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              <Map className="w-3 h-3 mr-1" />
              Public Map
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/alerts">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                <Bell className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Alerts</span>
              </Button>
            </Link>
            <Link to="/print-map" target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="print-map-btn">
                <Printer className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Print</span>
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-zinc-400 hover:text-white"
              data-testid="refresh-map-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Link to="/login">
              <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="public-login-btn">
                <Lock className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Personnel</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Status Bar */}
        <div className="flex items-center gap-4 px-4 lg:px-6 py-2 bg-zinc-800/50 overflow-x-auto">
          {Object.entries(itemTypeCounts).map(([type, count]) => (
            <Badge
              key={type}
              variant="outline"
              className={`whitespace-nowrap ${getItemColor(type)}`}
            >
              {getItemIcon(type)}
              <span className="ml-1 capitalize">{type.replace('_', ' ')}: {count}</span>
            </Badge>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-[100px] h-screen relative">
        {/* Map */}
        <div className="absolute inset-0 pt-[100px]">
          <EmergencyMap
            items={mapItems}
            height="100%"
            showLegend={true}
            onItemClick={setSelectedItem}
          />
        </div>

        {/* Alerts Panel */}
        {showAlerts && notifications.length > 0 && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="absolute top-4 right-4 w-80 z-[1000]"
          >
            <Card className="bg-zinc-900/95 backdrop-blur-xl border-zinc-800">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="font-headings text-sm uppercase tracking-wide text-white flex items-center gap-2">
                  <Bell className="w-4 h-4 text-red-400" />
                  Recent Alerts
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-zinc-400 hover:text-white"
                  onClick={() => setShowAlerts(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className="p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-xs ${getPriorityColor(notif.priority)}`}>
                            {notif.priority}
                          </Badge>
                        </div>
                        <h4 className="text-sm font-medium text-white">{notif.title}</h4>
                        <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{notif.message}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <Link to="/alerts" className="block mt-3">
                  <Button variant="outline" size="sm" className="w-full border-zinc-700 text-zinc-300 hover:text-white">
                    View All Alerts
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Show Alerts Button */}
        {!showAlerts && notifications.length > 0 && (
          <Button
            onClick={() => setShowAlerts(true)}
            className="absolute top-4 right-4 z-[1000] bg-red-500 hover:bg-red-600 text-white shadow-lg"
            data-testid="show-alerts-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            {notifications.length} Alerts
          </Button>
        )}

        {/* Active Items Panel */}
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          className="absolute bottom-4 left-4 w-80 z-[1000]"
        >
          <Card className="bg-zinc-900/95 backdrop-blur-xl border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="font-headings text-sm uppercase tracking-wide text-white flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                Active Incidents ({mapItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[150px]">
                {mapItems.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-4">No active incidents</p>
                ) : (
                  <div className="space-y-2">
                    {mapItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedItem(item)}
                        className={`w-full flex items-start gap-3 p-2 rounded-lg text-left transition-colors ${
                          selectedItem?.id === item.id
                            ? 'bg-zinc-700'
                            : 'bg-zinc-800/50 hover:bg-zinc-800'
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg ${getItemColor(item.item_type)}`}>
                          {getItemIcon(item.item_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-white truncate">{item.title}</h4>
                          <p className="text-xs text-zinc-500 truncate">{item.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </motion.div>

        {/* Back to Home */}
        <Link to="/" className="absolute top-4 left-4 z-[1000] lg:hidden">
          <Button variant="outline" size="sm" className="bg-zinc-900/95 border-zinc-700 text-zinc-300">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </Link>
      </main>
    </div>
  );
};

export default PublicMapPage;
